/**
 * StoreManager — Canonical store management UI for the active /store route.
 *
 * Route: App.jsx → /store → store.jsx → StoreManager.jsx
 * Backend: GraphQL via AppSync (GRAPHQL_URL in config.js)
 * Resolver: ss-api-store/src/v1/graphql/storeResolver.ts (on develop)
 *
 * SS-148 Acceptance Criteria (verified against this component):
 *   1. Photosets (ready) listed    — LIST_CREATOR_READY_COLLECTIONS query, left panel
 *   2. Create/update/delete items  — CREATE/UPDATE/DELETE_STORE_ITEM mutations + edit dialog
 *   3. Connect onboarding prompt   — store.jsx renders when storeEnabled is false
 *   4. Metrics displayed           — viewCount chip, price chip, preview image count chip
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, Stack, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Switch, FormControlLabel,
  Divider, Paper, Avatar, Select, MenuItem, FormControl, InputLabel, Autocomplete,
  CircularProgress, ButtonGroup, Alert, LinearProgress, CardMedia, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import StoreIcon from '@mui/icons-material/Store';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageIcon from '@mui/icons-material/Image';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import RefreshIcon from '@mui/icons-material/Refresh';
import { toast } from 'react-toastify';
import { ApolloClient, InMemoryCache, ApolloProvider, useQuery, useMutation, gql } from '@apollo/client';
import { S3Client, PutObjectCommand, GetObjectCommand } from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { fetchAuthSession } from 'aws-amplify/auth';
import { GRAPHQL_URL, WATERMARK_API_URL } from '../../config';
import awsConfig from '../../aws-config';
import { CollectionsApi } from "../../services/api/api";
import { useCognitoAuth } from '../../aws-amplify-auth';
import { useStoreWatermarkApply } from '../../hooks/useStoreWatermarkApply';

// GraphQL Queries and Mutations
const LIST_CREATOR_COLLECTIONS = gql`
  query ListCreatorReadyCollections($creatorUserId: ID!, $limit: Int) {
    listCreatorReadyCollections(creatorUserId: $creatorUserId, limit: $limit) {
      items {
        id
        title
        status
        previewImage
        createdAt
        hasLinkedStoreItems
      }
    }
  }
`;

const LIST_CREATOR_STORE_ITEMS = gql`
  query ListCreatorStoreItems($creatorUserId: ID!, $limit: Int) {
    listCreatorStoreItems(creatorUserId: $creatorUserId, limit: $limit) {
      items {
        id
        name
        description
        priceCents
        currency
        visible
        isR18
        creatorUserId
        collectionId
        status
        disabled
        viewCount
        updatedAt
        tags
        previewImage
        coverImage
        previewImages
        previewVideo
        creatorName
        creatorDisplayName
        creatorProfileImage
      }
    }
  }
`;

const CREATE_STORE_ITEM = gql`
  mutation CreateStoreItem($input: CreateStoreItemInput!) {
    createStoreItem(input: $input) {
      id
      name
      coverImage
      previewImages
      previewVideo
    }
  }
`;

const UPDATE_STORE_ITEM = gql`
  mutation UpdateStoreItem($id: ID!, $input: UpdateStoreItemInput!) {
    updateStoreItem(id: $id, input: $input) {
      id
      name
      coverImage
      previewImages
      previewVideo
      updatedAt
    }
  }
`;

const DELETE_STORE_ITEM = gql`
  mutation DeleteStoreItem($id: ID!) {
    deleteStoreItem(id: $id) {
      success
    }
  }
`;

// Apollo Client Setup
const createApolloClient = (token) => {
  return new ApolloClient({
    uri: GRAPHQL_URL,
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            listCreatorStoreItems: {
              merge(existing, incoming) {
                return incoming;
              },
            },
            listCreatorReadyCollections: {
              merge(existing, incoming) {
                return incoming;
              },
            },
          },
        },
      },
    }),
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
      query: {
        fetchPolicy: 'cache-and-network',
      },
    },
  });
};

// S3 Upload utility - use auth context for proper credentials
const uploadToS3 = async (file, folder = 'store/images', authContext) => {
  try {
    // Use the S3 client from auth context if available
    if (authContext?.s3Client) {
      console.log('Using S3 client from auth context');
      const fileExtension = file.name.split('.').pop();
      const fileName = `${folder}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExtension}`;

      // Convert File to ArrayBuffer for proper AWS SDK handling
      const fileBuffer = await file.arrayBuffer();

      const uploadParams = {
        Bucket: awsConfig.s3Buckets.public,
        Key: fileName,
        Body: new Uint8Array(fileBuffer),
        ContentType: file.type,
      };

      await authContext.s3Client.send(new PutObjectCommand(uploadParams));
      return `s3://${awsConfig.s3Buckets.public}/${fileName}`;
    }

    // Fallback to manual credential fetching
    console.log('Falling back to manual credential fetching');
    const session = await fetchAuthSession();
    
    if (!session.credentials) {
      throw new Error('No valid credentials available. Please log in again.');
    }

    const s3Client = new S3Client({
      region: awsConfig.awsRegion,
      credentials: session.credentials,
    });

    const fileExtension = file.name.split('.').pop();
    const fileName = `${folder}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExtension}`;

    // Convert File to ArrayBuffer for proper AWS SDK handling
    const fileBuffer = await file.arrayBuffer();

    const uploadParams = {
      Bucket: awsConfig.s3Buckets.public,
      Key: fileName,
      Body: new Uint8Array(fileBuffer),
      ContentType: file.type,
    };

    await s3Client.send(new PutObjectCommand(uploadParams));
    return `s3://${awsConfig.s3Buckets.public}/${fileName}`;
  } catch (error) {
    console.error('S3 upload failed:', error);
    throw new Error(`Failed to upload image to S3: ${error.message}`);
  }
};

// Image Upload Component
const ImageUpload = ({ label, value, onChange, accept = "image/*", multiple = false, authContext, onUploadStateChange, folder }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(multiple ? [] : null);
  const prevUploadingRef = useRef(false);

  // Notify parent component about upload state changes only when state actually changes
  useEffect(() => {
    if (uploading !== prevUploadingRef.current) {
      prevUploadingRef.current = uploading;
      if (onUploadStateChange) {
        onUploadStateChange(uploading);
      }
    }
  }, [uploading, onUploadStateChange]);
  useEffect(() => {
    // Resolve s3:// URIs to either direct HTTP(S) (if already public) or a signed GetObject URL
    let mounted = true;

    const toHttpsOrSigned = async (url) => {
      if (!url) return null;
      if (url.startsWith('http')) return url; // Already HTTP(S)

      // Handle S3 URIs
      if (url.startsWith('s3://')) {
        const s3Match = url.match(/s3:\/\/([^\/]+)\/(.*)/);
        if (s3Match) {
          const bucket = s3Match[1];
          const key = s3Match[2];

          try {
            // Prefer using the s3Client provided by authContext (if available)
            let s3Client = authContext?.s3Client;
            if (!s3Client) {
              // Fallback: fetch credentials from current auth session
              const session = await fetchAuthSession();
              if (!session?.credentials) {
                throw new Error('No valid credentials available to sign S3 URL');
              }
              s3Client = new S3Client({
                region: awsConfig.awsRegion,
                credentials: session.credentials,
              });
            }

            // Generate a presigned URL for GetObject (1 hour expiry)
            const command = new GetObjectCommand({ Bucket: bucket, Key: key });
            const signedUrl = await getSignedUrl(s3Client, command, { expiresIn: 3600 });
            return signedUrl;
          } catch (err) {
            // If signing fails, fallback to the standard https URL (may still 403)
            console.warn('Failed to generate signed S3 URL, falling back to public https URL:', err);
            return `https://${bucket}.s3.amazonaws.com/${key}`;
          }
        }
      }

      return url;
    };

    const resolvePreviews = async () => {
      if (multiple && Array.isArray(value)) {
        const resolved = await Promise.all(value.map(v => toHttpsOrSigned(v)));
        if (mounted) setPreview(resolved);
      } else if (!multiple && value) {
        const resolved = await toHttpsOrSigned(value);
        if (mounted) setPreview(resolved);
      } else {
        if (mounted) setPreview(multiple ? [] : null);
      }
    };

    resolvePreviews();

    return () => { mounted = false; };
  }, [value, multiple, authContext]);

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;
    // Enforce 3MB per-file limit
    const MAX_MB = 3;
    const tooLarge = files.find(f => f.size > MAX_MB * 1024 * 1024);
    if (tooLarge) {
      toast.error(`File "${tooLarge.name}" exceeds ${MAX_MB}MB limit.`);
      return;
    }

    setUploading(true);
    try {
      if (multiple) {
        const targetFolder = folder || 'store/preview-images';
        const uploadPromises = files.map(file => uploadToS3(file, targetFolder, authContext));
        const uploadedUrls = await Promise.all(uploadPromises);
        const newUrls = [...(value || []), ...uploadedUrls];
        onChange(newUrls);
        toast.success(`Uploaded ${files.length} image(s) successfully`);
      } else {
        const targetFolder = folder || 'store/cover-images';
        const uploadedUrl = await uploadToS3(files[0], targetFolder, authContext);
        onChange(uploadedUrl);
        toast.success('Cover image uploaded successfully');
      }
    } catch (error) {
      toast.error(error.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    if (multiple) {
      const newUrls = value.filter((_, i) => i !== index);
      onChange(newUrls);
    } else {
      onChange(null);
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
        <Button
          component="label"
          variant="outlined"
          startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
          disabled={uploading}
        >
          {uploading ? 'Uploading...' : label}
          <input
            type="file"
            hidden
            accept={accept}
            multiple={multiple}
            onChange={handleFileSelect}
          />
        </Button>
        {(multiple ? value?.length > 0 : value) && (
          <Typography variant="body2" color="text.secondary">
            {multiple ? `${value?.length || 0} image(s) uploaded` : 'Image uploaded'}
          </Typography>
        )}
      </Stack>

      {/* Preview */}
      {multiple ? (
        <Grid container spacing={1}>
          {preview.map((url, index) => (
            <Grid item xs={4} key={index}>
              <Card variant="outlined">
                <CardMedia
                  component="img"
                  height="80"
                  image={url}
                  alt={`Preview ${index + 1}`}
                  sx={{ objectFit: 'cover' }}
                />
                <Box sx={{ p: 1, textAlign: 'center' }}>
                  <IconButton size="small" color="error" onClick={() => removeImage(index)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        preview && (
          <Card variant="outlined" sx={{ maxWidth: 200 }}>
            <CardMedia
              component="img"
              height="120"
              image={preview}
              alt="Cover preview"
              sx={{ objectFit: 'cover' }}
            />
            <Box sx={{ p: 1, textAlign: 'center' }}>
              <IconButton size="small" color="error" onClick={() => removeImage()}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Card>
        )
      )}
    </Box>
  );
};

// Store Manager Component
function StoreManagerContent({ token }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const authContext = useCognitoAuth(); // Get auth context for S3 credentials
  const { applyAsset } = useStoreWatermarkApply(token);
  const [form, setForm] = useState({
    collectionId: '',
    name: '',
    description: '',
    priceCents: '',
    isR18: false,
    visible: true,
    currency: 'USD',
    tags: [],
    coverImage: null,
    previewImages: [],
    previewVideo: null
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [imagesUploading, setImagesUploading] = useState(false); // Track overall upload state
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaItems, setMediaItems] = useState([]); // { id, fileName }
  const [watermarkPreviewOpen, setWatermarkPreviewOpen] = useState(false);

  // Track uploading state from multiple ImageUpload components
  const [uploadStates, setUploadStates] = useState({
    coverImage: false,
    previewImages: false
  });

  const handleUploadStateChange = useCallback((uploadType, isUploading) => {
    setUploadStates(prev => ({
      ...prev,
      [uploadType]: isUploading
    }));
  }, []);

  // Create stable callbacks for each upload type
  const handleCoverImageUploadStateChange = useCallback((uploading) => {
    handleUploadStateChange('coverImage', uploading);
  }, [handleUploadStateChange]);

  const handlePreviewImagesUploadStateChange = useCallback((uploading) => {
    handleUploadStateChange('previewImages', uploading);
  }, [handleUploadStateChange]);

  // Check if any images are currently uploading (optimized to prevent unnecessary updates)
  useEffect(() => {
    const anyUploading = Object.values(uploadStates).some(Boolean);
    setImagesUploading(prevUploading => {
      // Only update if the state actually changed
      return prevUploading !== anyUploading ? anyUploading : prevUploading;
    });
  }, [uploadStates]);

  // Decode JWT for current user ID
  let CURRENT_USER_ID = 'UNKNOWN';
  try {
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      CURRENT_USER_ID = payload['cognito:username'] || payload['sub'] || 'UNKNOWN';
    }
  } catch { }

  // Load collection media when dialog opens and a collectionId is set (create or edit)
  useEffect(() => {
    const loadMedia = async () => {
      if (!dialogOpen || !form.collectionId) return;
      try {
        setMediaLoading(true);
        let authToken = token;
        if (typeof authToken === 'function') authToken = await authToken();
        const res = await CollectionsApi.getMedia(authToken, form.collectionId);
        if (res?.success) {
          setMediaItems(res.data || []);
        } else {
          toast.error(res?.message || 'Failed to load collection media');
        }
      } catch (e) {
        console.error('Failed to fetch media for collection', e);
        toast.error('Failed to load collection media');
      } finally {
        setMediaLoading(false);
      }
    };
    loadMedia();
  }, [dialogOpen, form.collectionId, editingId, token]);

  // Helpers to build S3 optimized URIs
  const isVideoFile = (name) => /\.(mp4|mov|m4v|webm|mkv|avi)$/i.test(name || '');
  const isImageFile = (name) => /\.(jpg|jpeg|png|webp|avif|heic|heif|tif|tiff)$/i.test(name || '');
  const toOptimizedS3Uri = (fileName) => {
    if (!fileName) return null;
    const uploads = awsConfig.s3Buckets.uploads;
    if (isVideoFile(fileName)) {
      // Keep original extension for videos
      return `s3://${uploads}/collections/${CURRENT_USER_ID}/${form.collectionId}/optimized/${fileName}`;
    }
    // Images are optimized as JPGs
    const base = fileName.replace(/\.[^/.]+$/, '');
    return `s3://${uploads}/collections/${CURRENT_USER_ID}/${form.collectionId}/optimized/${base}.jpg`;
  };

  // Apollo queries and mutations
  const { data: collectionsData, loading: collectionsLoading, refetch: refetchCollections } = useQuery(LIST_CREATOR_COLLECTIONS, {
    variables: { creatorUserId: CURRENT_USER_ID, limit: 100 },
    fetchPolicy: 'cache-and-network'
  });

  const { data: itemsData, loading: itemsLoading, refetch: refetchItems } = useQuery(LIST_CREATOR_STORE_ITEMS, {
    variables: { creatorUserId: CURRENT_USER_ID, limit: 100 },
    fetchPolicy: 'cache-and-network'
  });

  const [createStoreItem, { loading: creating }] = useMutation(CREATE_STORE_ITEM, {
    onCompleted: () => {
      toast.success('Store item created successfully');
      setDialogOpen(false);
      setEditingId(null);
      refetchItems();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create store item');
    }
  });

  const [updateStoreItem, { loading: updating }] = useMutation(UPDATE_STORE_ITEM, {
    onCompleted: () => {
      toast.success('Store item updated successfully');
      setDialogOpen(false);
      setEditingId(null);
      refetchItems();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update store item');
    }
  });

  const [deleteStoreItem] = useMutation(DELETE_STORE_ITEM, {
    onCompleted: () => {
      toast.success('Store item deleted successfully');
      refetchItems();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete store item');
    }
  });

  // Common tag options
  const commonTags = [
    'Photography', 'Cosplay', 'Gravure', 'Art', 'Digital Art', 'Portrait', 'Landscape', 'Abstract',
    'Nature', 'Street Photography', 'Fashion', 'Beauty', 'Vintage', 'Conceptual'
  ];

  // Supported currencies
  const currencies = [
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥' }
  ];

  const collections = collectionsData?.listCreatorReadyCollections?.items || [];
  const items = itemsData?.listCreatorStoreItems?.items || [];

  const listedCollectionIds = new Set(items.map(i => i.collectionId));
  const availableCollections = collections.filter(p => !listedCollectionIds.has(p.id));

  const openCreate = (p) => {
    setEditingId(null);
    setForm({
      collectionId: p?.id || '',
      name: p?.title || '',
      description: '',
      priceCents: '',
      isR18: false,
      visible: true,
      currency: 'USD',
      tags: [],
      coverImage: null,
      previewImages: [],
      previewVideo: null
    });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      collectionId: item.collectionId,
      name: item.name,
      description: item.description || '',
      // show price as decimal string for editing (e.g., 3.00)
      priceCents: ((item.priceCents || 0) / 100).toFixed(2),
      isR18: item.isR18,
      visible: item.visible,
      currency: item.currency || 'USD',
      tags: item.tags || [],
      coverImage: item.coverImage,
      previewImages: item.previewImages || [],
      previewVideo: item.previewVideo || null
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Note: Image uploads happen immediately for user feedback.
    // The S3 URLs are stored in form state and included in the mutation.
    // This provides better UX than waiting for form submission to upload images.
    const priceFloat = parseFloat(String(form.priceCents).replace(/[^0-9.]/g, ''));
    const priceCentsInt = Number.isFinite(priceFloat) ? Math.round(priceFloat * 100) : 0;

    const input = {
      name: form.name,
      description: form.description || undefined,
      priceCents: priceCentsInt,
      currency: form.currency || 'USD',
      isR18: form.isR18,
      visible: form.visible,
      tags: form.tags || [],
      coverImage: form.coverImage || undefined,
      previewImages: form.previewImages.length > 0 ? form.previewImages : undefined,
      previewVideo: form.previewVideo || undefined
    };

    // Only include collectionId for creation, not updates
    if (!editingId) {
      input.collectionId = form.collectionId;
    }

    if (editingId) {
      await updateStoreItem({
        variables: { id: editingId, input }
      });
    } else {
      await createStoreItem({
        variables: { input }
      });
    }
  };

  const handleQuickToggle = async (id, field, value) => {
    setBusyId(id);
    try {
      await updateStoreItem({
        variables: {
          id,
          input: { [field]: value }
        }
      });
    } catch (error) {
      toast.error(error.message || 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this store item?')) return;
    
    setBusyId(id);
    try {
      await deleteStoreItem({
        variables: { id }
      });
    } finally {
      setBusyId(null);
    }
  };

  const getImageUrl = (s3Uri) => {
    if (!s3Uri) return null;
    if (s3Uri.startsWith('http')) return s3Uri;
    
    // Convert S3 URI to HTTPS URL
    if (s3Uri.startsWith('s3://')) {
      const s3Match = s3Uri.match(/s3:\/\/([^\/]+)\/(.*)/);
      if (s3Match) {
        const bucket = s3Match[1];
        const key = s3Match[2];
        return `https://${bucket}.s3.amazonaws.com/${key}`;
      }
    }
    
    // Fallback: replace legacy format
    return s3Uri.replace('s3://', 'https://').replace(awsConfig.s3Buckets.uploads, `${awsConfig.s3Buckets.uploads}.s3.amazonaws.com`);
  };

  // Selection handlers for existing media
  const handleSelectCoverFromExisting = (fileName) => {
    const uri = toOptimizedS3Uri(fileName);
    setForm(f => ({ ...f, coverImage: uri }));
  };

  const handleTogglePreviewFromExisting = (fileName) => {
    const uri = toOptimizedS3Uri(fileName);
    if (!uri) return;
    setForm(f => {
      const exists = (f.previewImages || []).includes(uri);
      let next = exists ? f.previewImages.filter(u => u !== uri) : [...(f.previewImages || []), uri];
      // Enforce max 3 previews and images only
      next = next.filter(u => !/\.(mp4|mov|m4v|webm|mkv|avi)$/i.test(u));
      if (next.length > 3) {
        toast.warn('You can select up to 3 preview images.');
        next = next.slice(0, 3);
      }
      return { ...f, previewImages: next };
    });
  };

  const handleSelectPreviewVideo = (fileName) => {
    if (!isVideoFile(fileName)) {
      toast.warn('Please select a video file for preview video.');
      return;
    }
    const uri = toOptimizedS3Uri(fileName);
    setForm(f => ({ ...f, previewVideo: uri }));
  };

  if (collectionsLoading || itemsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'stretch', pt: 1, px: 0.5 }}>
      <Box sx={{ p: { xs: 1, sm: 2 }, maxWidth: { xs: 600, sm: 1000, md: 1200, lg: 1400 }, width: '100%', boxSizing: 'border-box' }}>
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                <StoreIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>Store Manager</Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage your store items!
                </Typography>
              </Box>
            </Stack>

            {/* Smaller refresh control */}
            <Tooltip title="Refresh">
              <IconButton
                size="small"
                onClick={() => {
                  refetchCollections();
                  refetchItems();
                }}
                aria-label="refresh"
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Paper>

        <Grid container spacing={3}>
          {/* Available Collections */}
          <Grid item xs={12} lg={5}>
            <Paper elevation={1} sx={{ p: 2, height: 'fit-content' }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <AddIcon color="primary" />
                <Typography variant="h6">Listable Collections</Typography>
                <Chip label={availableCollections.length} size="small" />
              </Stack>
              <Stack spacing={2} sx={{ maxHeight: 600, overflow: 'auto' }}>
                {availableCollections.map(p => (
                  <Card key={p.id} variant="outlined" sx={{ 
                    '&:hover': { 
                      boxShadow: 4, 
                      transform: 'translateY(-2px)',
                      transition: 'all 0.2s ease-in-out'
                    } 
                  }}>
                    <CardContent sx={{ pb: 2 }}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        {p.previewImage && (
                          <Avatar 
                            src={getImageUrl(p.previewImage)} 
                            sx={{ width: 48, height: 48, borderRadius: 1 }}
                            variant="rounded"
                          />
                        )}
                        <Box sx={{ flex: 1 }}>
                          <Typography fontWeight={600} sx={{ mb: 0.5 }}>{p.title}</Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip label={p.status == "Ready for Distribution" ? "Ready" : p.status} size="small" color="primary" variant="outlined" />
                            <Typography variant="caption" color="text.secondary">
                              {new Date(p.createdAt).toLocaleDateString()}
                            </Typography>
                          </Stack>
                        </Box>
                        <Button 
                          variant="contained" 
                          size="small" 
                          onClick={() => openCreate(p)}
                          sx={{ minWidth: 50 }}
                        >
                          List
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
                {availableCollections.length === 0 && (
                  <Alert severity="info">
                    No collections available to list. Create or mark a collection as "Ready for Distribution" to add new store items.
                  </Alert>
                )}
              </Stack>
            </Paper>
          </Grid>

          {/* Store Items */}
          <Grid item xs={12} lg={7}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <LocalOfferIcon color="primary" />
                <Typography variant="h6">Your Store Items</Typography>
                <Chip label={items.length} size="small" />
              </Stack>
              <Stack spacing={2} sx={{ maxHeight: 600, overflow: 'auto' }}>
                {items.map(i => {
                  const currency = currencies.find(c => c.code === i.currency) || currencies[0];
                  const formattedPrice = `${currency.symbol}${(i.priceCents / 100).toFixed(2)}`;
                  const coverImageUrl = getImageUrl(i.coverImage || i.previewImage);
                  
                  return (
                    <Card key={i.id} variant="outlined" sx={{ 
                      opacity: !i.visible ? 0.6 : 1,
                      '&:hover': { boxShadow: 2 }
                    }}>
                      <CardContent sx={{ pb: 2 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          {coverImageUrl && (
                            <Avatar 
                              src={coverImageUrl} 
                              sx={{ width: 56, height: 56, borderRadius: 1 }}
                              variant="rounded"
                            />
                          )}
                          <Box sx={{ flex: 1 }}>
                            <Typography fontWeight={600} sx={{ mb: 1 }}>{i.name}</Typography>
                            <Stack direction="row" spacing={1} mb={1} flexWrap="wrap">
                              {i.isR18 && <Chip label="R18" color="error" size="small" />}
                              {!i.visible && <Chip label="Hidden" size="small" color="default" />}
                              <Chip 
                                label={formattedPrice} 
                                size="small" 
                                color="success"
                                icon={<CurrencyExchangeIcon />}
                              />
                              <Chip 
                                label={`${i.viewCount || 0} views`} 
                                size="small" 
                                variant="outlined"
                              />
                              {i.coverImage && <Chip label="Custom Cover" size="small" color="secondary" />}
                              {i.previewImages?.length > 0 && (
                                <Chip 
                                  label={`${i.previewImages.length} preview images`} 
                                  size="small" 
                                  color="info"
                                  icon={<PhotoLibraryIcon />}
                                />
                              )}
                              {i.previewVideo && (
                                <Chip label="Has preview video" size="small" color="secondary" />
                              )}
                            </Stack>
                            {i.tags && i.tags.length > 0 && (
                              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                {i.tags.slice(0, 3).map((tag, idx) => (
                                  <Chip 
                                    key={idx} 
                                    label={tag} 
                                    size="small" 
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                  />
                                ))}
                                {i.tags.length > 3 && (
                                  <Chip 
                                    label={`+${i.tags.length - 3}`} 
                                    size="small" 
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                  />
                                )}
                              </Stack>
                            )}
                          </Box>
                          <ButtonGroup orientation="vertical" size="small">
                            <Tooltip title="Edit Details">
                              <IconButton 
                                disabled={busyId === i.id} 
                                onClick={() => openEdit(i)}
                                sx={{ 
                                  bgcolor: 'primary.main', 
                                  color: 'white',
                                  '&:hover': { bgcolor: 'primary.dark' }
                                }}
                              >
                                <EditIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={i.visible ? 'Hide from Store' : 'Show in Store'}>
                              <IconButton 
                                disabled={busyId === i.id} 
                                onClick={() => handleQuickToggle(i.id, 'visible', !i.visible)}
                                color={i.visible ? 'warning' : 'success'}
                              >
                                {i.visible ? <VisibilityOffIcon fontSize="inherit" /> : <VisibilityIcon fontSize="inherit" />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Item">
                              <IconButton 
                                color="error" 
                                disabled={busyId === i.id} 
                                onClick={() => handleDelete(i.id)}
                              >
                                <DeleteIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          </ButtonGroup>
                        </Stack>
                        {busyId === i.id && <LinearProgress sx={{ mt: 1 }} />}
                      </CardContent>
                    </Card>
                  );
                })}
                {items.length === 0 && (
                  <Alert severity="info">
                    No store items yet. List your first collection to start selling your work.
                  </Alert>
                )}
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        {/* Enhanced Form Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullScreen={fullScreen} fullWidth maxWidth="lg" component="form" onSubmit={handleSubmit}>
          <DialogTitle sx={{ pb: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <StoreIcon />
              <Typography variant="h6">
                {editingId ? 'Edit Store Item' : 'List New Store Item'}
              </Typography>
              {editingId && (
                <Chip label="Editing" color="primary" size="small" />
              )}
            </Stack>
          </DialogTitle>
          <DialogContent dividers sx={{ py: 3, maxHeight: { xs: '70vh', sm: '75vh', md: '80vh' }, overflowY: 'auto' }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Stack spacing={3}>
                  <TextField 
                    label="Collection ID" 
                    value={form.collectionId} 
                    disabled 
                    required 
                    size="small"
                    helperText={editingId ? "Cannot change collection for existing items" : "Set by selecting a collection from the list"}
                  />
                  <TextField 
                    label="Item Name" 
                    value={form.name} 
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))} 
                    required 
                    size="small"
                    helperText="Display name for your store item"
                  />
                  <TextField 
                    label="Description" 
                    value={form.description} 
                    multiline 
                    rows={3} 
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))} 
                    size="small"
                    helperText="Optional description of your item"
                  />
                  
                  <Stack direction="row" spacing={2}>
                    <TextField 
                      label="Price" 
                      type="number" 
                      inputProps={{ step: 0.01, min: 0 }}
                      value={form.priceCents} 
                      onChange={e => setForm(f => ({ ...f, priceCents: e.target.value }))} 
                      required 
                      size="small"
                      sx={{ flex: 1 }}
                      helperText="Enter a decimal amount like 3.00"
                    />
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Currency</InputLabel>
                      <Select 
                        value={form.currency} 
                        label="Currency"
                        onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
                      >
                        {currencies.map(currency => (
                          <MenuItem key={currency.code} value={currency.code}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography>{currency.symbol}</Typography>
                              <Typography>{currency.code}</Typography>
                            </Stack>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                  
                  <Autocomplete
                    multiple
                    options={commonTags}
                    value={form.tags}
                    onChange={(event, newValue) => {
                      setForm(f => ({ ...f, tags: newValue }));
                    }}
                    freeSolo
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip 
                            key={key}
                            variant="outlined" 
                            label={option} 
                            size="small"
                            {...tagProps} 
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tags"
                        placeholder="Add tags..."
                        size="small"
                        helperText="Add tags to help customers find your item"
                      />
                    )}
                  />
                  
                  <Stack spacing={1}>
                    <FormControlLabel 
                      control={
                        <Switch 
                          checked={form.isR18} 
                          onChange={e => setForm(f => ({ ...f, isR18: e.target.checked }))} 
                          color="error"
                        />
                      } 
                      label="R18 Content" 
                    />
                    <FormControlLabel 
                      control={
                        <Switch 
                          checked={form.visible} 
                          onChange={e => setForm(f => ({ ...f, visible: e.target.checked }))} 
                          color="success"
                        />
                      } 
                      label="Visible in Store" 
                    />
                  </Stack>
                </Stack>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Stack spacing={3}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <ImageIcon />
                      <Typography variant="h6">Cover Image</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Upload a custom cover image for your store item. This will be displayed prominently in the store.
                    </Typography>
                    <ImageUpload
                      label="Upload Cover Image"
                      value={form.coverImage}
                      onChange={async (url) => {
                        const watermarkedUrl = await applyAsset({
                          originalUrl: url,
                          assetType: 'cover',
                          collectionId: form.collectionId,
                        });
                        setForm(f => ({ ...f, coverImage: watermarkedUrl }));
                      }}
                      authContext={authContext}
                      onUploadStateChange={handleCoverImageUploadStateChange}
                      folder={`store/${form.collectionId || 'unassigned'}/cover-images`}
                    />
                    {
                      <Box sx={{ mt: 2 }}>
                        <Divider sx={{ mb: 2 }}>or select from existing</Divider>
                        {mediaLoading ? (
                          <LinearProgress />
                        ) : (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Choose one media from the collection (uses optimized asset)
                            </Typography>
                            <Stack spacing={1} sx={{ maxHeight: 180, overflow: 'auto', mt: 1 }}>
                              {mediaItems.map(m => (
                                <Stack key={m.id} direction="row" alignItems="center" spacing={1}>
                                  <Button size="small" variant={form.coverImage === toOptimizedS3Uri(m.fileName) ? 'contained' : 'outlined'} onClick={() => handleSelectCoverFromExisting(m.fileName)}>
                                    Select
                                  </Button>
                                  <Typography variant="body2" noWrap sx={{ flex: 1 }} title={m.fileName}>{m.fileName}</Typography>
                                </Stack>
                              ))}
                              {mediaItems.length === 0 && (
                                <Typography variant="body2" color="text.secondary">No media found in this collection.</Typography>
                              )}
                            </Stack>
                          </Box>
                        )}
                      </Box>
                    }
                  </Paper>
                  
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Preview Images
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Upload additional preview images to showcase your work. These will be displayed in the item details.
                    </Typography>
                    <Alert severity="info" sx={{ mb: 2, borderRadius: "8px", "& .MuiAlert-action": { flexWrap: "nowrap" } }}>
                      <Typography variant="body2">
                        Preview images use your default ShieldShot watermark settings.
                      </Typography>
                      <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setWatermarkPreviewOpen(true)}
                        >
                          Preview
                        </Button>
                        <Button
                          size="small"
                          variant="text"
                          href="/settings#watermark"
                        >
                          Edit
                        </Button>
                      </Box>
                    </Alert>
                    <ImageUpload
                      label="Upload Preview Images"
                      value={form.previewImages}
                      onChange={async (urls) => {
                        const watermarked = await Promise.all(
                          urls.map(async (url) => {
                            if (url.includes('/watermarked/')) return url;
                            return applyAsset({
                              originalUrl: url,
                              assetType: 'preview',
                              collectionId: form.collectionId,
                            });
                          }),
                        );
                        setForm(f => ({ ...f, previewImages: watermarked }));
                      }}
                      multiple
                      authContext={authContext}
                      onUploadStateChange={handlePreviewImagesUploadStateChange}
                      folder={`store/${form.collectionId || 'unassigned'}/previews`}
                    />
                    {
                      <Box sx={{ mt: 2 }}>
                        <Divider sx={{ mb: 1 }}>or select from existing</Divider>
                        <Typography variant="caption" color="text.secondary">
                          Select up to 3 images (uses optimized assets)
                        </Typography>
                        {mediaLoading ? (
                          <LinearProgress sx={{ mt: 1 }} />
                        ) : (
                          <Stack spacing={0.5} sx={{ maxHeight: 180, overflow: 'auto', mt: 1 }}>
                            {mediaItems.filter(m => isImageFile(m.fileName)).map(m => {
                              const uri = toOptimizedS3Uri(m.fileName);
                              const checked = (form.previewImages || []).includes(uri);
                              return (
                                <Stack key={m.id} direction="row" alignItems="center" spacing={1}>
                                  <FormControlLabel
                                    control={<Switch size="small" checked={checked} onChange={() => handleTogglePreviewFromExisting(m.fileName)} />}
                                    label={<Typography variant="body2" noWrap title={m.fileName}>{m.fileName}</Typography>}
                                  />
                                </Stack>
                              );
                            })}
                            {mediaItems.filter(m => isImageFile(m.fileName)).length === 0 && (
                              <Typography variant="body2" color="text.secondary">No images found in this collection.</Typography>
                            )}
                          </Stack>
                        )}
                      </Box>
                    }
                  </Paper>

                  {/* Optional Preview Video */}
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Preview Video (optional)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Pick one video from the collection to feature. Uses optimized asset.
                    </Typography>
                    {mediaLoading ? (
                      <LinearProgress />
                    ) : (
                      <Stack spacing={1} sx={{ maxHeight: 160, overflow: 'auto' }}>
                        {mediaItems.filter(m => isVideoFile(m.fileName)).map(m => (
                          <Stack key={m.id} direction="row" alignItems="center" spacing={1}>
                            <Button size="small" variant={form.previewVideo === toOptimizedS3Uri(m.fileName) ? 'contained' : 'outlined'} onClick={() => handleSelectPreviewVideo(m.fileName)}>
                              {form.previewVideo === toOptimizedS3Uri(m.fileName) ? 'Selected' : 'Select'}
                            </Button>
                            <Typography variant="body2" noWrap title={m.fileName} sx={{ flex: 1 }}>{m.fileName}</Typography>
                          </Stack>
                        ))}
                        {mediaItems.filter(m => isVideoFile(m.fileName)).length === 0 && (
                          <Typography variant="body2" color="text.secondary">No videos found in this collection.</Typography>
                        )}
                      </Stack>
                    )}
                  </Paper>
                </Stack>
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 3 }} />
            
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>💡 Enhancement Tips:</strong><br/>
                • Images are uploaded immediately for better user experience<br/>
                • Upload a compelling cover image to attract customers<br/>
                • Add multiple preview images to showcase your work<br/>
                • Use descriptive names and detailed descriptions<br/>
                • Set competitive pricing for your market
              </Typography>
            </Alert>

            {imagesUploading && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  <strong>⏳ Images are currently uploading...</strong><br/>
                  Please wait for uploads to complete before creating the store item.
                </Typography>
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setDialogOpen(false)} size="large">
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={creating || updating || imagesUploading} 
              variant="contained" 
              size="large"
              startIcon={creating || updating || imagesUploading ? <CircularProgress size={16} /> : (editingId ? <EditIcon /> : <AddIcon />)}
            >
              {imagesUploading ? 'Uploading Images...' : 
               creating || updating ? (editingId ? 'Updating...' : 'Creating...') : 
               (editingId ? 'Update Item' : 'Create Item')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={watermarkPreviewOpen}
          onClose={() => setWatermarkPreviewOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>ShieldShot Watermark Preview</DialogTitle>
          <DialogContent>
            <WatermarkPreviewContent token={token} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setWatermarkPreviewOpen(false)}>Close</Button>
            <Button variant="contained" href="/settings#watermark">Edit Settings</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}

function WatermarkPreviewContent({ token }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(`${WATERMARK_API_URL}/v1/distributor/default-profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`Failed to load watermark config: ${res.status}`);
        const data = await res.json();
        if (!cancelled) setConfig(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (token) load();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!config) return null;

  return (
    <Box>
      <Typography variant="body2" gutterBottom>
        Your store preview/cover/thumbnail images will be watermarked with these defaults:
      </Typography>
      <Box component="ul" sx={{ pl: 2, mb: 2 }}>
        <Typography component="li" variant="body2">Text: <strong>{config.text}</strong></Typography>
        <Typography component="li" variant="body2">Preset: {config.preset}</Typography>
        <Typography component="li" variant="body2">Subject Shield: {config.subjectShield ? 'On' : 'Off'}</Typography>
        <Typography component="li" variant="body2">Buffer: {config.bufferPx}px</Typography>
        <Typography component="li" variant="body2">Apply to: {Object.entries(config.applyTo || {}).filter(([, v]) => v).map(([k]) => k).join(', ') || 'none'}</Typography>
        <Typography component="li" variant="body2">ShieldShot badge: {config.badgeRemoval ? 'Removed' : 'Shown'}</Typography>
      </Box>
      {config.logoKey && (
        <Typography variant="body2">Logo: {config.logoKey}</Typography>
      )}
    </Box>
  );
}

// Main component with Apollo Provider
export default function StoreManager({ token }) {
  const apolloClient = createApolloClient(token);

  return (
    <ApolloProvider client={apolloClient}>
      <StoreManagerContent token={token} />
    </ApolloProvider>
  );
}
